import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userSearchService, StudentDetail } from '../services/userSearchService';
import { ArrowLeftIcon } from '../components/Icons';

const statusLabels: Record<string, { text: string; className: string }> = {
  PENDING: { text: '待完成', className: 'bg-surface-container-high text-on-surface-variant' },
  IN_PROGRESS: { text: '进行中', className: 'bg-yellow-100 text-yellow-700' },
  COMPLETED: { text: '已完成', className: 'bg-green-100 text-green-700' },
};

const StudentDetailPage: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<StudentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'enrollments' | 'questionnaires' | 'alerts'>('enrollments');

  useEffect(() => {
    if (studentId) {
      fetchDetail();
    }
  }, [studentId]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await userSearchService.getStudentDetail(Number(studentId));
      setDetail(data);
    } catch (err: any) {
      setError(err.response?.data?.message || '加载学生详情失败');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dt: string | null) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-on-surface mb-2 font-headline">{error || '学生不存在'}</h2>
          <button
            onClick={() => navigate(-1)}
            className="text-primary-600 hover:text-primary-700"
          >
            返回搜索
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-on-surface-variant hover:text-on-surface mb-4"
      >
        <ArrowLeftIcon size={20} className="mr-2" />
        返回搜索
      </button>

      {/* Title area */}
      <div className="mb-6">
        <div className="flex items-center space-x-3">
          <h1 className="text-2xl font-bold text-on-surface font-headline">{detail.name}</h1>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            学生
          </span>
          {detail.showStudentDetails && detail.genderDisplay && (
            <span className="text-sm text-on-surface-variant font-label">{detail.genderDisplay}</span>
          )}
          {detail.hasCrisisAlert && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              预警
            </span>
          )}
        </div>
      </div>

      {/* Basic info card */}
      <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-5 mb-6">
        <h3 className="text-sm font-semibold text-on-surface mb-3 font-label">
          基本信息
          {!detail.showStudentDetails && (
            <span className="ml-2 text-xs font-normal text-on-surface-variant">（该校已关闭敏感信息显示）</span>
          )}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm font-body">
          {detail.showStudentDetails && (
            <div>
              <span className="text-on-surface-variant font-label">用户ID：</span>
              <span className="text-on-surface">{detail.idCardNumber}</span>
            </div>
          )}
          {detail.showStudentDetails && detail.studentNumber && (
            <div>
              <span className="text-on-surface-variant font-label">学号：</span>
              <span className="text-on-surface">{detail.studentNumber}</span>
            </div>
          )}
          {detail.showStudentDetails && detail.phone && (
            <div>
              <span className="text-on-surface-variant font-label">电话：</span>
              <span className="text-on-surface">{detail.phone}</span>
            </div>
          )}
          <div>
            <span className="text-on-surface-variant font-label">账号状态：</span>
            <span className={detail.hasUserAccount ? 'text-green-600' : 'text-on-surface-variant'}>
              {detail.hasUserAccount ? '已创建' : '未创建'}
            </span>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="mb-6" style={{ borderBottom: '1px solid rgba(171, 179, 183, 0.2)' }}>
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab('enrollments')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'enrollments'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            就读历史
          </button>
          <button
            onClick={() => setActiveTab('questionnaires')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'questionnaires'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            问卷记录
          </button>
          {detail.hasCrisisAlert && (
            <button
              onClick={() => setActiveTab('alerts')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'alerts'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-red-500 hover:text-red-700'
              }`}
            >
              预警信息
            </button>
          )}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'enrollments' && (
        <div>
          {detail.enrollments.length === 0 ? (
            <div className="text-center py-12 bg-surface-container-lowest rounded-2xl shadow-soft">
              <p className="text-on-surface-variant font-body">暂无就读记录</p>
            </div>
          ) : (
            <div className="bg-surface-container-lowest rounded-2xl shadow-soft overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-surface-container-low">
                  <tr>
                    <th className="px-4 py-3 text-left text-on-surface-variant font-medium font-label">学年</th>
                    <th className="px-4 py-3 text-left text-on-surface-variant font-medium font-label">学校</th>
                    <th className="px-4 py-3 text-left text-on-surface-variant font-medium font-label">年级</th>
                    <th className="px-4 py-3 text-left text-on-surface-variant font-medium font-label">班级</th>
                    <th className="px-4 py-3 text-left text-on-surface-variant font-medium font-label">状态</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-container-low">
                  {detail.enrollments.map((e, idx) => (
                    <tr key={idx} className={e.current ? 'bg-blue-50' : ''}>
                      <td className="px-4 py-3 text-on-surface font-body">{e.academicYear}</td>
                      <td className="px-4 py-3 text-on-surface font-body">{e.schoolName}</td>
                      <td className="px-4 py-3 text-on-surface font-body">{e.grade || '-'}</td>
                      <td className="px-4 py-3 text-on-surface font-body">{e.className}</td>
                      <td className="px-4 py-3">
                        {e.current ? (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 font-medium">当前</span>
                        ) : (
                          <span className="text-on-surface-variant text-xs font-label">历史</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'questionnaires' && (
        <div>
          {!detail.hasUserAccount ? (
            <div className="text-center py-12 bg-surface-container-lowest rounded-2xl shadow-soft">
              <p className="text-on-surface-variant font-body">该学生尚未创建账号，无问卷记录</p>
            </div>
          ) : detail.questionnaireHistory.length === 0 ? (
            <div className="text-center py-12 bg-surface-container-lowest rounded-2xl shadow-soft">
              <p className="text-on-surface-variant font-body">暂无问卷记录</p>
            </div>
          ) : (
            <div className="bg-surface-container-lowest rounded-2xl shadow-soft overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-surface-container-low">
                  <tr>
                    <th className="px-4 py-3 text-left text-on-surface-variant font-medium font-label">问卷编号</th>
                    <th className="px-4 py-3 text-left text-on-surface-variant font-medium font-label">问卷名称</th>
                    <th className="px-4 py-3 text-left text-on-surface-variant font-medium font-label">状态</th>
                    <th className="px-4 py-3 text-left text-on-surface-variant font-medium font-label">提交时间</th>
                    <th className="px-4 py-3 text-left text-on-surface-variant font-medium font-label">指标结论</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-container-low">
                  {detail.questionnaireHistory.map((q) => {
                    const st = statusLabels[q.status] || statusLabels.PENDING;
                    return (
                      <tr key={q.questionnaireId} className={q.hasCrisisAlert ? 'bg-red-50' : ''}>
                        <td className="px-4 py-3 text-on-surface-variant text-xs font-mono">{q.questionnaireCode || '-'}</td>
                        <td className="px-4 py-3 text-on-surface font-body">
                          <div className="flex items-center space-x-2">
                            <span>{q.title}</span>
                            {q.hasCrisisAlert && (
                              <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700 font-medium">预警</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.className}`}>{st.text}</span>
                        </td>
                        <td className="px-4 py-3 text-on-surface-variant font-label">{formatDateTime(q.submittedAt)}</td>
                        <td className="px-4 py-3">
                          {q.indicators.length === 0 ? (
                            <span className="text-on-surface-variant font-label">-</span>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {q.indicators.map((ind, idx) => (
                                <span
                                  key={idx}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    ind.crisisAlert
                                      ? 'bg-red-100 text-red-800 ring-1 ring-red-300'
                                      : 'bg-surface-container-high text-on-surface'
                                  }`}
                                  title={`${ind.name}: ${ind.score ?? '-'}分`}
                                >
                                  {ind.name}: {ind.conclusionTitle || `${ind.score ?? '-'}分`}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'alerts' && detail.hasCrisisAlert && (
        <div>
          {detail.questionnaireHistory.filter(q => q.hasCrisisAlert).length === 0 ? (
            <div className="text-center py-12 bg-surface-container-lowest rounded-2xl shadow-soft">
              <p className="text-on-surface-variant font-body">暂无预警信息</p>
            </div>
          ) : (
            <div className="space-y-4">
              {detail.questionnaireHistory
                .filter(q => q.hasCrisisAlert)
                .map((q) => (
                  <div key={q.questionnaireId} className="bg-surface-container-lowest rounded-2xl shadow-soft overflow-hidden" style={{ border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                    <div className="bg-red-50 px-5 py-3" style={{ borderBottom: '1px solid rgba(239, 68, 68, 0.2)' }}>
                      <div className="flex items-center space-x-3">
                        {q.questionnaireCode && (
                          <span className="text-xs text-on-surface-variant font-mono font-label">{q.questionnaireCode}</span>
                        )}
                        <span className="font-medium text-on-surface">{q.title}</span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">预警</span>
                        {q.submittedAt && (
                          <span className="text-xs text-on-surface-variant font-label">提交于 {formatDateTime(q.submittedAt)}</span>
                        )}
                      </div>
                    </div>
                    <div className="px-5 py-4">
                      <div className="space-y-3">
                        {q.indicators
                          .filter(ind => ind.crisisAlert)
                          .map((ind, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-red-50 rounded-xl px-4 py-3">
                              <div className="flex items-center space-x-4">
                                <span className="px-2.5 py-1 rounded text-xs font-medium bg-red-100 text-red-800 ring-1 ring-red-300">
                                  {ind.name}
                                </span>
                                <span className="text-sm text-on-surface font-body">
                                  得分: <span className="font-semibold">{ind.score ?? '-'}</span>
                                </span>
                              </div>
                              <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
                                {ind.conclusionTitle || '预警'}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentDetailPage;
